/**
 * Central time formatter for AROMA NET.
 * Server stores timestamps in UTC — every user-facing render must be forced
 * to Asia/Jakarta (WIB, UTC+07:00) instead of relying on the browser clock.
 */
const TZ = "Asia/Jakarta";

function toDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Full WIB timestamp: "16 Agu 2026, 08.30" (id-ID locale, Asia/Jakarta).
 * Options can extend/override the defaults.
 */
export function formatWIB(input, opts = {}) {
  const d = toDate(input);
  if (!d) return "-";
  return d.toLocaleString("id-ID", {
    timeZone: TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...opts,
  });
}

/** Short WIB timestamp used in tables (no year, no seconds). */
export function formatWIBShort(input) {
  return formatWIB(input, { year: undefined, month: "short" });
}

/** WIB date only. */
export function formatWIBDate(input, opts = {}) {
  const d = toDate(input);
  if (!d) return "-";
  return d.toLocaleDateString("id-ID", {
    timeZone: TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...opts,
  });
}

/** WIB clock only, HH:mm:ss with colon separators (spec literal). */
export function formatWIBClock(input) {
  const d = toDate(input);
  if (!d) return "--:--:--";
  // en-GB gives HH:mm:ss with colons even for Indonesian users (spec requires colons).
  return d.toLocaleTimeString("en-GB", {
    timeZone: TZ,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Human-readable WIB timestamp with 24-hour colon separator, e.g.
 *   "21 Agu 2026, 16:20"
 *
 * Different from `formatWIB` which uses id-ID's default "." between HH and mm.
 * Used in tables/lists where we need a compact "DD MMM YYYY, HH:mm" — matches
 * the spec for completed_at rendering.
 */
export function formatWIBDateTime24(input) {
  const d = toDate(input);
  if (!d) return "-";
  const datePart = d.toLocaleDateString("id-ID", {
    timeZone: TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-GB", {
    timeZone: TZ,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
}

/**
 * Same as {@link formatWIBDateTime24} but drops the year for space-constrained
 * card layouts (Kanban DONE badge): "21 Agu, 16:20".
 */
export function formatWIBDateTime24Short(input) {
  const d = toDate(input);
  if (!d) return "-";
  const datePart = d.toLocaleDateString("id-ID", {
    timeZone: TZ,
    day: "2-digit",
    month: "short",
  });
  const timePart = d.toLocaleTimeString("en-GB", {
    timeZone: TZ,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
}

/** Long Indonesian day + date, e.g. "Minggu, 16 Agustus 2026". */
export function formatWIBLongDate(input) {
  const d = toDate(input);
  if (!d) return "-";
  return d.toLocaleDateString("id-ID", {
    timeZone: TZ,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export const WIB_TIMEZONE = TZ;

// ---------------------------------------------------------------------------
// WIB (Asia/Jakarta, UTC+7) split helpers for custom date/time pickers.
// Stored server-side as ISO 8601 UTC; the UI works with WIB local `YYYY-MM-DD`
// and `HH:mm` pairs so the value never round-trips through the browser's own
// timezone (which can silently shift the date, especially in containers).
// ---------------------------------------------------------------------------

/** Split an ISO datetime (or Date) into `{ date, time }` in WIB. Empty input
 *  returns empty strings so callers can bind directly to inputs. */
export function splitIsoToWibParts(input) {
  const d = toDate(input);
  if (!d) return { date: "", time: "" };
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).reduce((acc, x) => (acc[x.type] = x.value, acc), {});
  const hh = p.hour === "24" ? "00" : p.hour;
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${hh}:${p.minute}` };
}

/** Join a WIB `YYYY-MM-DD` + `HH:mm` back to an ISO 8601 string carrying the
 *  Asia/Jakarta offset (+07:00), which the backend can store verbatim as UTC
 *  without ambiguity. Returns `null` when either half is empty/invalid. */
export function joinWibPartsToIso(date, time) {
  if (!date || !time) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (!/^\d{2}:\d{2}$/.test(time)) return null;
  const [hh, mm] = time.split(":").map(Number);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return `${date}T${time}:00+07:00`;
}


/** Bulan berjalan WIB "YYYY-MM" (tidak bergantung zona waktu browser). */
export function currentWibPeriode(now = new Date()) {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit" })
    .formatToParts(now).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}`;
}
